import React from 'react';
import KickImage from 'Reuseables/KickImage';
import RelatedProductItem from '../../Atoms/RelatedProductItem';

import { IRelatedProductProps } from './RelatedProduct.definitions';

/** CSS import */
import Styles from './RelatedProduct.module.scss';
import EmptyImage from 'images/emptyimage.png';
import { categoryURLSearchPage, convertBrandNameToSlug } from 'utils/Helpers';
import { RectShape, TextBlock } from 'react-placeholder/lib/placeholders';
import ReactPlaceholder from 'react-placeholder/lib';
import Spacer from 'Pages/Home/Components/Atoms/Spacer';
import { useGetViewType } from 'Pages/Home/Hooks/useGetViewType';
import Arrow from './Components/Arrow';
import { formatCurrency } from 'utils';

const CustomPlaceholder: React.FC = () => {
  return (
    <div className={Styles['placeholder']}>
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={`popular-placeholder-${index}`}
          className={Styles['placeholder-item']}
        >
          <TextBlock rows={2} color="#eeeeee" style={{ width: '50%' }} />
          <Spacer height={12} />
          <RectShape color="#eeeeee" style={{ width: '100%', height: 147 }} />
        </div>
      ))}
    </div>
  );
};

const RelatedProduct: React.FC<IRelatedProductProps> = (
  props: IRelatedProductProps,
) => {
  const { data, isReady, isFetching } = props;
  const itemScroll = React.useRef<HTMLDivElement>(null);
  const [scrollLeftValue, setScrollLeftValue] = React.useState<number>(0);
  const { isMobileView } = useGetViewType();
  const [hovered, isHovered] = React.useState(false);

  const onScrollItems = () => {
    if (!itemScroll.current) {
      return;
    }

    const {
      current: { scrollWidth, clientWidth, scrollLeft },
    } = itemScroll;

    const scrollLeftCeil = Math.ceil(scrollLeft);
    const maxScrollWidth = scrollWidth - clientWidth;

    if (scrollLeftCeil % 100 === 0) {
      setScrollLeftValue(scrollLeftCeil);
    }

    if (scrollLeftCeil >= maxScrollWidth) {
      setScrollLeftValue(scrollLeftCeil);
    }

    if (scrollLeft === 0) {
      setScrollLeftValue(0);
    }
  };

  const isShowingArrow = (arrow: 'left' | 'right'): boolean => {
    if (!itemScroll.current) {
      return false;
    }

    if (itemScroll.current.scrollWidth <= itemScroll.current.clientWidth) {
      return false;
    }

    if (isMobileView) {
      return false;
    }

    if (arrow === 'left') {
      return scrollLeftValue > 0;
    }

    if (arrow === 'right') {
      const maxScrollWidth =
        itemScroll.current.scrollWidth - itemScroll.current.clientWidth;
      return scrollLeftValue < maxScrollWidth;
    }

    return false;
  };

  const rightArrowClicked = () => {
    if (!itemScroll.current) {
      return;
    }

    const scrolledNow = Math.ceil(itemScroll.current.scrollLeft);
    const maxScrollWidth =
      itemScroll.current.scrollWidth - itemScroll.current.clientWidth;

    if (scrolledNow >= maxScrollWidth) {
      return;
    }

    const interval = setInterval(() => {
      if (!itemScroll.current) {
        return;
      }

      itemScroll.current.scrollLeft += isMobileView ? 2 : 4;
    }, 1);

    setTimeout(() => {
      clearInterval(interval);

      if (!itemScroll.current) {
        return;
      }

      setScrollLeftValue(Math.ceil(itemScroll.current.scrollLeft));
    }, 1000);
  };

  const leftArrowClicked = () => {
    if (!itemScroll.current) {
      return;
    }

    const scrolledNow = Math.ceil(itemScroll.current.scrollLeft);
    const maxScrollWidth = 0;

    if (scrolledNow <= maxScrollWidth) {
      return;
    }

    const interval = setInterval(() => {
      if (!itemScroll.current) {
        return;
      }

      itemScroll.current.scrollLeft -= isMobileView ? 2 : 4;
    }, 1);

    setTimeout(() => {
      clearInterval(interval);

      if (!itemScroll.current) {
        return;
      }

      setScrollLeftValue(Math.ceil(itemScroll.current.scrollLeft));
    }, 1000);
  };

  if (!isFetching && !data?.length) {
    return <div />;
  }
  return (
    <div className={Styles['wrapper']}>
      <h2 className={Styles['title']}>Related Product</h2>
      <div
        className={Styles['product-list']}
        onMouseEnter={() => isHovered(true)}
        onMouseLeave={() => isHovered(false)}
      >
        <ReactPlaceholder
          customPlaceholder={<CustomPlaceholder />}
          ready={isReady || data.length > 0}
        >
          <Arrow
            type="left"
            onClick={leftArrowClicked}
            show={hovered && isShowingArrow('left')}
          />
          <div
            className={Styles['item-wrapper']}
            ref={itemScroll}
            onScroll={onScrollItems}
          >
            {data.map((item) => (
              <RelatedProductItem
                key={item.id}
                name={item.display_name}
                price={formatCurrency(item?.price)}
                image={
                  item.product_variant_images?.[0].URL || EmptyImage
                }
                slug={item.slug}
                brandSlug={convertBrandNameToSlug(
                  item.product.brand.flat?.[0] || item.product.brand.slug,
                )}
                category={categoryURLSearchPage(item.product.category.name)}
              />
            ))}
          </div>
          <Arrow
            type="right"
            onClick={rightArrowClicked}
            show={hovered && isShowingArrow('right')}
          />
        </ReactPlaceholder>
      </div>
    </div>
  );
};

export default RelatedProduct;
